import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useGetAiChatBotHistoryQuery,
  useSendAiChatBotMessageMutation,
  useCreateLeadFromChatBotMutation,
  useStartNewAiChatBotConversationMutation,
  useGetMyAiChatBotConversationsQuery,
  useDeleteAiChatBotConversationMutation,
  useLazyGetAiChatBotConversationDetailQuery,
} from "../../../app/services/book/aiChatBot"
import Avatar from "../Chatbox/Avatar"

const formatTime = (value) => {
  const d = value ? new Date(value) : new Date()
  if (isNaN(d.getTime())) return ""
  const pad = (n) => String(n).padStart(2, "0")
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} | ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const OfferCard = ({ offer }) => (
  <div className="border border-slate-200 rounded-lg p-3 bg-white shadow-sm">
    <div className="font-semibold text-slate-800">{offer.hotel_name || "—"}</div>
    <div className="text-sm text-slate-500">
      {[offer.resort_name, offer.star_name].filter(Boolean).join(" · ")}
    </div>
    <div className="text-sm text-slate-500">
      {offer.from_date} — {offer.to_date} ({offer.nights} nights)
    </div>
    <div className="mt-1 flex items-center justify-between gap-2">
      <span className="font-medium text-slate-900">
        {offer.price != null ? `${offer.price} ${offer.currency || ""}`.trim() : "—"}
      </span>
      {offer.book_url && (
        <a
          href={offer.book_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-medium px-2.5 py-1 rounded-md bg-blue-600 text-white hover:bg-blue-700"
        >
          Book
        </a>
      )}
    </div>
  </div>
)

const MessageBubble = ({ message }) => {
  const isBot = message.role === "bot"
  const isOwn = !isBot
  const offers = message.meta?.offers || []

  return (
    <div className={`flex gap-2.5 mb-3 ${isOwn ? "justify-end" : "justify-start"}`}>
      {!isOwn && <Avatar className="w-8 h-8 mt-4" company="AI" />}

      <div className={`flex flex-col max-w-[80%] ${isOwn ? "items-end" : "items-start"}`}>
        <span className="text-xs font-semibold text-slate-500 mb-0.5 px-1">
          {isOwn ? "" : "AI Bot"}
        </span>

        <div
          className={`border shadow-sm px-4 py-2.5 ${
            isOwn ? "bg-orange-50/30 border-orange-200" : "bg-slate-50 border-slate-200"
          }`}
          style={{ borderRadius: isOwn ? "18px 4px 18px 18px" : "4px 18px 18px 18px" }}
        >
          <p className="text-[15px] leading-[1.5] whitespace-pre-wrap break-words text-slate-900">
            {message.content}
          </p>

          {offers.length > 0 && (
            <div className="mt-2 grid gap-2">
              {offers.map((offer) => (
                <OfferCard key={`${offer.token}-${offer.price_id}`} offer={offer} />
              ))}
            </div>
          )}
        </div>

        <span className="text-[11px] text-slate-400 mt-0.5 px-1">
          {formatTime(message.created_at)}
        </span>
      </div>
    </div>
  )
}

const LOADING_TIPS = [
  ["admin.ai_chat_bot_tip_ram", "Downloading more RAM..."],
  ["admin.ai_chat_bot_tip_isp", "Tip your internet provider..."],
  ["admin.ai_chat_bot_tip_splines", "Reticulating splines..."],
  ["admin.ai_chat_bot_tip_hamsters", "Convincing the hamsters to run faster..."],
  ["admin.ai_chat_bot_tip_multiverse", "Searching every hotel in the multiverse..."],
  ["admin.ai_chat_bot_tip_wifi", "Untangling the WiFi cables..."],
  ["admin.ai_chat_bot_tip_almost", "Almost there, promise..."],
  ["admin.ai_chat_bot_tip_camels", "Negotiating with a camel for a better rate..."],
]

const TypingIndicator = ({ reassuranceTip }) => {
  const { t } = useTranslation()

  return (
    <div className="flex gap-2.5 mb-3 justify-start">
      <Avatar className="w-8 h-8 mt-4" company="AI" />
      <div
        className="border border-slate-200 bg-slate-50 px-4 py-3 flex items-center gap-2"
        style={{ borderRadius: "4px 18px 18px 18px" }}
      >
        <div className="flex items-center gap-1">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce"
              style={{ animationDelay: `${i * 0.12}s` }}
            />
          ))}
        </div>
        {reassuranceTip && (
          <span className="text-xs text-slate-500">
            {t(reassuranceTip[0], reassuranceTip[1])}
          </span>
        )}
      </div>
    </div>
  )
}

const AiChatBotPage = () => {
  const { t } = useTranslation()
  const { data: history, isLoading: isHistoryLoading, refetch } = useGetAiChatBotHistoryQuery()
  const { data: myConversations, refetch: refetchMyConversations } = useGetMyAiChatBotConversationsQuery()
  const [sendMessage, { isLoading: isSending }] = useSendAiChatBotMessageMutation()
  const [createLead, { isLoading: isCreatingLead }] = useCreateLeadFromChatBotMutation()
  const [startNewConversation, { isLoading: isStartingNew }] = useStartNewAiChatBotConversationMutation()
  const [fetchConversationDetail] = useLazyGetAiChatBotConversationDetailQuery()
  const [deleteConversation, { isLoading: isDeletingConversation }] = useDeleteAiChatBotConversationMutation()
  const [text, setText] = useState("")
  const [messages, setMessages] = useState([])
  const [conversationId, setConversationId] = useState(null)
  const [leadFeedback, setLeadFeedback] = useState(null)
  const [reassuranceTip, setReassuranceTip] = useState(null)
  const bottomRef = useRef(null)
  const textareaRef = useRef(null)

  useEffect(() => {
    if (history?.messages) {
      setMessages(history.messages)
      setConversationId(history.conversation_id)
    }
  }, [history])

  useEffect(() => {
    if (!isSending) {
      setReassuranceTip(null)
      return
    }

    const timer = setTimeout(() => {
      setReassuranceTip(LOADING_TIPS[Math.floor(Math.random() * LOADING_TIPS.length)])
    }, 4000)
    return () => clearTimeout(timer)
  }, [isSending])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, isSending])

  const handleSend = async () => {
    const value = text.trim()
    if (!value || isSending) return

    setMessages((prev) => [
      ...prev,
      {
        id: `local-${Date.now()}`,
        role: "user",
        content: value,
        meta: {},
        created_at: new Date().toISOString(),
      },
    ])
    setText("")

    try {
      const result = await sendMessage({ text: value, conversation_id: conversationId }).unwrap()
      setConversationId(result.conversation_id)
      setMessages((prev) => [
        ...prev,
        {
          id: `bot-${Date.now()}`,
          role: "bot",
          content: result.reply,
          meta: { offers: result.offers || [] },
          created_at: new Date().toISOString(),
        },
      ])
      refetchMyConversations()
    } catch (_error) {
      setMessages((prev) => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          role: "bot",
          content: t("main.error_occurred", "Something went wrong."),
          meta: {},
          created_at: new Date().toISOString(),
        },
      ])
    }
  }

  const handleNewChat = async () => {
    try {
      const result = await startNewConversation().unwrap()
      setConversationId(result.conversation_id)
      setMessages([])
      setLeadFeedback(null)
      refetchMyConversations()
    } catch (_error) {
      // ignore — user can retry
    }
  }

  const handleSelectConversation = async (id) => {
    if (id === conversationId) return
    try {
      const result = await fetchConversationDetail({ bundle_id: id }).unwrap()
      setConversationId(result.conversation_id)
      setMessages(result.messages)
      setLeadFeedback(null)
    } catch (_error) {
      // ignore — user can retry
    }
  }

  const handleDeleteConversation = async (event, id) => {
    event.stopPropagation()
    if (!window.confirm(t("admin.ai_chat_bot_delete_confirm", "Delete this conversation?"))) return

    try {
      await deleteConversation(id).unwrap()
      refetchMyConversations()

      if (id === conversationId) {
        await handleNewChat()
      }
    } catch (_error) {
      // ignore — user can retry
    }
  }

  const handleCreateLead = async () => {
    setLeadFeedback(null)
    try {
      const result = await createLead({}).unwrap()
      setLeadFeedback({ type: "success", leadId: result.lead_id })
      refetch()
    } catch (error) {
      setLeadFeedback({
        type: "error",
        message: error?.data?.error || t("main.error_occurred", "Something went wrong."),
      })
    }
  }

  return (
    <div className="max-w-5xl mx-auto p-4 flex gap-4 h-[80vh]">
      <div className="w-56 shrink-0 flex flex-col border border-slate-200 rounded-lg bg-white overflow-hidden">
        <button
          type="button"
          onClick={handleNewChat}
          disabled={isStartingNew}
          className="m-2 px-3 py-2 rounded-md bg-slate-800 text-white text-sm font-medium disabled:opacity-50"
        >
          + {t("admin.ai_chat_bot_new_chat", "New chat")}
        </button>
        <div className="flex-1 overflow-y-auto px-2 pb-2">
          {(myConversations?.conversations || []).map((conversation) => (
            <div
              key={conversation.id}
              className={`group w-full flex items-center gap-1 px-2 py-2 rounded-md text-xs mb-1 ${
                conversation.id === conversationId
                  ? "bg-blue-50 text-blue-800"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <button
                type="button"
                onClick={() => handleSelectConversation(conversation.id)}
                className="flex-1 min-w-0 text-left"
              >
                <div className="truncate font-medium">
                  {conversation.preview || t("admin.ai_chat_bot_new_chat", "New chat")}
                </div>
                <div className="text-[10px] text-slate-400">{formatTime(conversation.updated_at)}</div>
              </button>
              <button
                type="button"
                onClick={(event) => handleDeleteConversation(event, conversation.id)}
                disabled={isDeletingConversation}
                title={t("admin.ai_chat_bot_delete_chat", "Delete")}
                aria-label={t("admin.ai_chat_bot_delete_chat", "Delete")}
                className="shrink-0 w-5 h-5 flex items-center justify-center rounded text-slate-400 opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 transition-opacity"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-lg font-semibold text-slate-800">
            {t("admin.ai_chat_bot_title", "AI Chat Search")}
          </h1>
          <div className="flex items-center gap-2">
            {/*
            {leadFeedback?.type === "success" && (
              <span className="text-xs text-emerald-600 font-medium">
                {t("admin.lead_created", "Lead")} #{leadFeedback.leadId}
              </span>
            )}
            {leadFeedback?.type === "error" && (
              <span className="text-xs text-rose-600 font-medium">{leadFeedback.message}</span>
            )}
            <button
              type="button"
              onClick={handleCreateLead}
              disabled={isCreatingLead}
              className="text-sm px-3 py-1.5 rounded-md bg-emerald-600 text-white disabled:opacity-50"
            >
              {t("admin.create_lead", "Create lead")}
            </button>
            */}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto border border-slate-200 rounded-lg p-3 bg-slate-100">
          {isHistoryLoading ? (
            <div className="text-sm text-slate-400">{t("admin.loading_dots")}</div>
          ) : (
            messages.map((message) => <MessageBubble key={message.id} message={message} />)
          )}
          {isSending && <TypingIndicator reassuranceTip={reassuranceTip} />}
          <div ref={bottomRef} />
        </div>

        <div className="mt-3 flex gap-2">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault()
                handleSend()
              }
            }}
            rows={1}
            placeholder={t(
              "admin.ai_chat_bot_placeholder",
              'Ask for a tour, e.g. "2+1, Kemer, 7 nights"'
            )}
            className="flex-1 border border-slate-300 rounded-md px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-[15px] leading-relaxed"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={isSending || !text.trim()}
            className="px-4 py-2 rounded-md bg-blue-600 text-white disabled:opacity-50 self-end"
          >
            {t("main.send", "Send")}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AiChatBotPage
